import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import PageHeader from "../../components/common/PageHeader";
import ExperienceHeroSection from "../../components/UserComponents/ExperienceHeroSection";
import ExperienceInfo from "../../components/UserComponents/ExperienceInfo";
import ExperienceAvailability from "../../components/UserComponents/ExperienceAvailability";
import VendorInfo from "../../components/UserComponents/VendorInfo";
import ExperienceDetailsSkeleton from "../../components/common/ExperienceDetailsSkeleton";
import ReviewBookingModal from "../User/ReviewBookingPage";
import GuestBookingModal from "./GuestBookingModal";
import { toast } from "react-toastify";

import {
  usePublicExperienceDetails,
  useValidateBookingGuest,
} from "../../custom-hooks/queries";

export default function ExperienceDetailsPage() {
  const { experienceSlug } = useParams();

  const [selectedDate, setSelectedDate] = useState(null);
  const [people, setPeople] = useState(1);
  const [selectedAddOnIds, setSelectedAddOnIds] = useState([]);
  const [email, setEmail] = useState("");

  const [isGuestModalOpen, setIsGuestModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [selectedStartTime, setSelectedStartTime] = useState("");

  const { data, isLoading } = usePublicExperienceDetails(experienceSlug);
  const experience = data?.data || {};

  const specificStartTime = experience?.specificStartTime;

  /* =========================
     DURATION ESTIMATION
  ========================= */
  const { estimatedDuration, durationError } = useMemo(() => {
    if (!people) return { estimatedDuration: null, durationError: null };

    const rules = experience.escalationRules || [];

    if (!rules.length) {
      return {
        estimatedDuration: experience.baseDurationMinutes,
        durationError: null,
      };
    }

    const matched = rules.find(
      (r) => people >= r.minGuests && people <= r.maxGuests,
    );

    if (!matched) {
      return {
        estimatedDuration: null,
        durationError: "Group size not supported",
      };
    }

    return {
      estimatedDuration: matched.durationMinutes,
      durationError: null,
    };
  }, [people, experience]);

  /* =========================
     ADD-ONS TOTAL
  ========================= */
  const addOnsTotal = useMemo(() => {
    return (experience.addsOn || [])
      .filter((a) => selectedAddOnIds.includes(a.experienceAddOnId))
      .reduce((sum, a) => sum + Number(a.price || 0), 0);
  }, [experience.addsOn, selectedAddOnIds]);

  /* =========================
     TOTAL PRICE
  ========================= */
  const totalPrice = useMemo(() => {
    const base = Number(experience.price || 0);
    return base * people + addOnsTotal;
  }, [experience.price, people, addOnsTotal]);

  /* =========================
     VALIDATE (GUEST)
  ========================= */
  const {
    mutate: validateGuest,
    data: validationData,
    isPending,
  } = useValidateBookingGuest();

  /* =========================
     BOOK NOW
  ========================= */
  const handleBookNow = () => {
    if (!selectedDate) {
      toast.error("Please select a date");
      return;
    }

    if (durationError) {
      toast.error(durationError);
      return;
    }

    setIsGuestModalOpen(true);
  };

  const handleGuestValidate = (email) => {
    validateGuest(
      {
        experienceId: experience.experienceId,
        selectedDate,
        nofPersons: people,
        addOnIds: selectedAddOnIds,
        userEmail: email,
        selectedStartTime: specificStartTime ? selectedStartTime : null,
      },
      {
        onSuccess: () => {
          setIsGuestModalOpen(false);
          setIsReviewModalOpen(true);
        },
      },
    );
  };

  if (isLoading) return <ExperienceDetailsSkeleton />;

  return (
    <div className="container mx-auto py-10 px-6">
      <PageHeader title="Experience" />

      <ExperienceHeroSection
        heroImage={experience.mainImage}
        galleryImages={experience.gallery || []}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-10 items-start">
        {/* LEFT */}
        <div className="lg:col-span-2 space-y-6">
          <ExperienceInfo
            title={experience.title}
            location={experience.location}
            description={experience.description}
            category={experience.category}
            price={experience.price}
          />

          {experience.vendor && (
            <VendorInfo
              name={experience.vendor.businessName}
              description={experience.vendor.description}
              logoUrl={experience.vendor.logoUrl}
              rating={experience.vendor.ratingAvg}
              totalReviews={experience.vendor.totalReviews}
            />
          )}
        </div>

        {/* RIGHT */}
        <ExperienceAvailability
          calendarMeta={experience.calendarMeta}
          availabilityRules={experience.availability}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          people={people}
          setPeople={setPeople}
          estimatedDuration={estimatedDuration}
          durationError={durationError}
          addOns={experience.addsOn || []}
          selectedAddOnIds={selectedAddOnIds}
          setSelectedAddOnIds={setSelectedAddOnIds}
          price={experience.price}
          onBookNow={handleBookNow}
          loading={isPending}
          isConnected={experience.vendor?.isConnected}
          specificStartTime={experience.specificStartTime}
          startTimes={experience?.startTimes}
          selectedStartTime={selectedStartTime}
          setSelectedStartTime={setSelectedStartTime}
        />
      </div>

      {/* GUEST EMAIL MODAL */}
      <GuestBookingModal
        isOpen={isGuestModalOpen}
        onClose={() => setIsGuestModalOpen(false)}
        onValidateBooking={handleGuestValidate}
        email={email}
        setEmail={setEmail}
        isLoading={isPending}
      />

      {/* REVIEW MODAL (FULL ALTERNATE LOGIC WORKS HERE) */}
      <ReviewBookingModal
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        experience={experience}
        validationData={validationData}
        selectedDate={selectedDate}
        people={people}
        basePrice={experience.price}
        priceType={experience.priceType}
        totalPrice={totalPrice}
        email={email}
        successMessage={validationData?.success_message}
        specificStartTime={specificStartTime}
      />
    </div>
  );
}
